import {
  type LIST_VIEWER_VARIANT,
  type OWNERS_TYPE,
  type REQUEST_OPTION_TYPE,
} from './constants';

export interface EndpointParams {
  max: number;
  offset: number;
  payload?: object;
  queryParams?: object;
}

export interface ApplicationListResponse {
  applicationId: number;
  applicationName: string;
  applicationDescription: string;
  applicationLogo: string;
  applicationDisplayName: string | null;
  securitysystem: string;
  isChecked: boolean;
  accountCreationAllowed?: boolean;
  recommended?: boolean;
}

export interface AccountOwnerListResponse {
  type: (typeof OWNERS_TYPE)[keyof typeof OWNERS_TYPE];
  id: number;
  // User
  userid: number;
  firstname: string;
  lastname: string;
  email: string;
  username: string;
  displayname?: string;
  description?: string;
  // user group
  usergroupkey: number;
  user_groupdescription: string;
  user_groupname: string;
  users: {
    userkey: number;
    username: string;
    firstname: string | null;
    lastname: string | null;
    city: string;
    email: string;
  }[];
  // Common
  rank?: number | null;
  isChecked: boolean;
}

export interface RequestOptionConfig {
  key: number | string;
  requestOption: (typeof REQUEST_OPTION_TYPE)[keyof typeof REQUEST_OPTION_TYPE];
}

export interface EntitlementAccess {
  data: {
    entitlementtype: string;
    entitlementtypekey: number;
    entitlement_valuekey: number;
    entitlement_value: string;
    displayname?: string;
    description?: string;
    peergroupconfidencepercentage: string;
    endpointkey: number;
    endpointname: string;
    soxcritical: string;
    mandatoryBusinessJustification?: boolean;
    recommended?: boolean | string;
    isChecked?: boolean;
    startdate?: string | null;
    enddate?: string | null;
    isStartDateDisabled?: boolean;
    justification?: string;
    type?: string;
  }[];
  entitlementTypeConfigs?: Record<
    string,
    {
      entitlementTypeID: string;
      entitlementTypeDisplayName: string;
      isStartDate: boolean;
      isEndDate: boolean;
      isStartDateMandatory: boolean;
      isEndDateMandatory: boolean;
      isEndDateExtendable: boolean;
      defaultTimeFrameHrs: string | null;
      maxTimeFrameHrs: string | null;
      entitlementTypeRequestOptionConfig?: RequestOptionConfig;
    }
  >;
  error: string;
  success: boolean;
  totalRecords: number;
}

export interface EnterpriseRoleAccess {
  data: {
    rolename: string;
    description?: string;
    displayname?: string;
    roletype: number;
    roleid: number;
    soxcritical: string;
    mandatoryBusinessJustification?: boolean;
    recommended?: boolean | string;
    isChecked?: boolean;
    startdate?: string | null;
    enddate?: string | null;
    isStartDateDisabled?: boolean;
    justification?: string;
    type?: string;
  }[];
  roleRequestOptionConfig?: RequestOptionConfig;
  enterpriseRoleDateConfig?: {
    isStartDate: boolean;
    isEndDate: boolean;
    isStartDateMandatory: boolean;
    isEndDateMandatory: boolean;
    isEndDateExtendable: boolean;
  };
  error: string;
  success: boolean;
  totalRecords: number;
}

export interface ApplicationRoleAccess {
  data: {
    rolename: string;
    description?: string;
    displayname?: string;
    roletype: number;
    roleid: number;
    mandatoryBusinessJustification?: boolean;
    soxcritical: string;
    recommended?: boolean | string;
    isChecked?: boolean;
    startdate?: string | null;
    enddate?: string | null;
    isStartDateDisabled?: boolean;
    justification?: string;
    type?: string;
  }[];
  roleRequestOptionConfig?: RequestOptionConfig;
  applicationRoleDateConfig?: {
    isStartDate: boolean;
    isEndDate: boolean;
    isStartDateMandatory: boolean;
    isEndDateMandatory: boolean;
    isEndDateExtendable: boolean;
  };
  error: string;
  success: boolean;
  totalRecords: number;
}

export enum AccessTypeMap {
  entitlement = 'ENTITLEMENT',
  enterpriseRole = 'ENTERPRISE_ROLE',
  applicationRole = 'APPLICATION_ROLE',
}

export interface AccessConfigMap {
  [AccessTypeMap.entitlement]: EntitlementAccess['entitlementTypeConfigs'];
  [AccessTypeMap.enterpriseRole]: EnterpriseRoleAccess['enterpriseRoleDateConfig'];
  [AccessTypeMap.applicationRole]: ApplicationRoleAccess['applicationRoleDateConfig'];
}

export type EntitlementAccessData = EntitlementAccess['data'];
export type EnterpriseRoleAccessData = EnterpriseRoleAccess['data'];
export type ApplicationRoleAccessData = ApplicationRoleAccess['data'];

export interface AccessListResponseMap {
  [AccessTypeMap.entitlement]: EntitlementAccessData;
  [AccessTypeMap.enterpriseRole]: EnterpriseRoleAccessData;
  [AccessTypeMap.applicationRole]: ApplicationRoleAccessData;
}

export type AccessListResponse = (
  | EntitlementAccessData
  | EnterpriseRoleAccessData
  | ApplicationRoleAccessData
)[number];

// Update review card
export interface ServiceAccountListResponse {
  creator: string;
  endpointKey: number;
  accounttype: string;
  updateUser: string;
  description: string | null;
  createdon: string;
  userKey: string;
  accountKey: number;
  endpointname: string;
  updatedate: string;
  accountowner: {
    rank: number;
    type: string;
    value: string;
  }[];
  name: string | null;
  endpointLogo: string | null;
  status: string;
  username: string;
  isChecked: boolean;
}

// All custom modal types
export type CustomListModalTypes =
  | ApplicationListResponse
  | AccountOwnerListResponse
  | AccessListResponse
  | ServiceAccountListResponse;

export type ListVariantType =
  (typeof LIST_VIEWER_VARIANT)[keyof typeof LIST_VIEWER_VARIANT];
